import React from "react";
import { Flame, Activity, Moon, CalendarDays, Drumstick, Trophy } from "lucide-react";
import { fmtDuration } from "@/lib/format";

const TONE = {
  carb: "#34C759", energy: "#FF9500", rest: "#A1A1AA",
  week: "#4C9AFF", protein: "#4C9AFF", race: "#FF3B30",
};

function buildInsights(targets, raceWeek, profile) {
  const days = targets?.daily || [];
  if (!days.length) return [];
  const out = [];

  const trained = days.filter((d) => (d.trainingMinutes || 0) > 0);

  // Biggest fuelling day
  const maxCarb = [...days].sort((a, b) => b.carb - a.carb)[0];
  if (maxCarb && maxCarb.carb > 0) {
    out.push({ icon: Flame, tone: "carb",
      text: `Your biggest fuelling day is ${maxCarb.day} (${maxCarb.carb}g carbs). Front-load breakfast and refuel within 30–60 min of finishing.` });
  }

  // Hardest training day
  if (trained.length) {
    const maxTrain = [...trained].sort((a, b) => b.trainingMinutes - a.trainingMinutes)[0];
    out.push({ icon: Activity, tone: "energy",
      text: `${maxTrain.day} is your hardest session (${fmtDuration(maxTrain.trainingMinutes)} · ${maxTrain.energy} kcal target). Carbs before, protein after.` });
  }

  // Rest / low days
  const rest = days.filter((d) => d.demand === "rest" || (d.trainingMinutes || 0) === 0);
  if (rest.length) {
    out.push({ icon: Moon, tone: "rest",
      text: `${rest.length} lower-fuel ${rest.length > 1 ? "days" : "day"} (${rest.map((d) => d.day).join(", ")}). Carbs ease back for recovery — lean on protein and veg.` });
  }

  // Weekly volume
  const totalMin = days.reduce((s, d) => s + (d.trainingMinutes || 0), 0);
  if (totalMin > 0) {
    out.push({ icon: CalendarDays, tone: "week",
      text: `${(totalMin / 60).toFixed(1)} h training this week (${targets.weeklyDemand} load), averaging ${targets.weekAverage.energy} kcal/day.` });
  }

  // Protein per kg
  const wt = Number(profile?.weight);
  if (wt > 0 && targets.weekAverage?.protein) {
    const perKg = (targets.weekAverage.protein / wt).toFixed(1);
    out.push({ icon: Drumstick, tone: "protein",
      text: `Protein averages ~${perKg} g/kg/day — spread it across 3–4 meals for the best muscle repair.` });
  }

  // Race week
  if (raceWeek?.enabled) {
    const raceDay = days[raceWeek.dayIndex]?.day || "race day";
    out.push({ icon: Trophy, tone: "race",
      text: `Race week: aim for ${raceWeek.carbsPerHour}g carbs/hr on ${raceDay} and carb-load the 24–48 h before.` });
  }

  return out.slice(0, 6);
}

export default function PlanInsights({ targets, raceWeek, profile }) {
  const insights = buildInsights(targets, raceWeek, profile);
  if (!insights.length) return null;

  return (
    <div className="border border-white/10 bg-[#141414] p-5 mb-8 print-day" data-testid="plan-insights">
      <div className="grid sm:grid-cols-2 gap-3">
        {insights.map((ins, i) => {
          const Icon = ins.icon;
          return (
            <div key={i} data-testid={`insight-${i}`} className="flex items-start gap-3">
              <span className="grid place-items-center h-7 w-7 shrink-0 border" style={{ borderColor: `${TONE[ins.tone]}66`, color: TONE[ins.tone] }}>
                <Icon className="h-3.5 w-3.5" />
              </span>
              <p className="text-[13px] text-zinc-200 leading-relaxed">{ins.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
